/**
 * Square-style customer sales receipt — bitmap renderer.
 *
 * Renders a checkout to a PNG `data:` URL via the shared canvas toolkit, ready
 * for `starPrintService.printImage`.
 *
 * Layout (top → bottom):
 *   business name (bold, left)
 *   address (left)            date / time / cashier (right)
 *   IN STORE / TO GO                 Order: <orderNumber>
 *   Customer: <saved name> · Table <dine-in table> (optional)
 *   Receipt: <shortId>
 *   FAILED PAYMENT (for a failed sale)
 *   items: name (left) ............. line price (right), modifiers indented
 *   Subtotal / Taxes & Fees / Tip ...
 *   TOTAL (bold) + payment line(s), each with an indented "Refunded −$x" note
 *   ── REFUNDED / PARTIALLY REFUNDED ── (settled refunds only)
 *   refund line(s) · Total Refunded · Net Total
 *   custom footer · QR → point-craft.com/contact · PointCraft branding
 */
import {
  CheckoutDataType,
  CheckoutItemType,
  SourceStatusEnum,
  SaleOrderDetails,
  PaymentMethodEnum,
  BusinessDataV2Type,
  getSaleOrderDetails,
  mergeSaleOrderDetails,
  FinancialPaymentTransactionType,
} from '@am3nd-org/pointcraft-types';

import { fPhone, fCurrency } from 'src/utils/format-number';

import { resolveReceiptFooterText } from 'src/store/DeviceSync/selectors';

import type { PaperWidth } from './types';
import { renderQrCanvas, createReceiptCanvas } from './canvas-toolkit';
import {
  summarizeRefunds,
  findRefundParent,
  selectSettledRefunds,
  selectCapturedPayments,
  getSaleReceiptChannelBand,
} from './sale-receipt.helpers';

const CONTACT_URL = 'https://point-craft.com/contact';

// ────────────────────────────────────────────────────────────────────────────
// Data helpers
// ────────────────────────────────────────────────────────────────────────────

function toDate(value: unknown): Date {
  if (value instanceof Date) return value;
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) return d;
  }
  if (value && typeof (value as any).toDate === 'function') return (value as any).toDate();
  return new Date();
}

/** Human-friendly payment label, mirroring the legacy receipt builder. */
function paymentLabel(tx: FinancialPaymentTransactionType): string {
  const { cardType } = tx as any;
  if (cardType === 'DEBIT') return 'Debit Card';
  if (cardType === 'CREDIT') return 'Credit Card';
  switch (tx.type) {
    case PaymentMethodEnum.CARD:
    case PaymentMethodEnum.MANUAL_CARD:
    case PaymentMethodEnum.BLE_CONNECTED_READER:
      return 'Credit Card';
    case PaymentMethodEnum.CASH:
      return 'Cash';
    case PaymentMethodEnum.EBT:
    case PaymentMethodEnum.EBT_CASH:
      return 'EBT';
    case PaymentMethodEnum.DEBT:
      return 'Buy Now Pay Later';
    case PaymentMethodEnum.OTHER:
      return 'Other';
    default:
      return 'Payment';
  }
}

function last4Of(tx: FinancialPaymentTransactionType): string | undefined {
  return (tx as any).last4CardNumber || (tx as any).last4 || undefined;
}

function paymentLineLabel(tx: FinancialPaymentTransactionType): string {
  const l4 = last4Of(tx);
  return l4 ? `${paymentLabel(tx)} ****${l4}` : paymentLabel(tx);
}

function modifierNames(item: CheckoutItemType): string[] {
  const groups = item.metadata?.selectedModifierData ?? [];
  return groups
    .flatMap((group) => group.selected ?? [])
    .map((mod) => (mod.quantity && mod.quantity > 1 ? `${mod.name} (×${mod.quantity})` : mod.name))
    .filter(Boolean);
}

// ────────────────────────────────────────────────────────────────────────────
// Renderer
// ────────────────────────────────────────────────────────────────────────────

export async function saleReceiptCanvas(
  checkout: CheckoutDataType,
  business: BusinessDataV2Type,
  opts: { paperWidth?: PaperWidth; orderDetails?: SaleOrderDetails | null } = {}
): Promise<string> {
  const c = createReceiptCanvas({ paperWidth: opts.paperWidth });
  const { px } = c;

  // Pre-generate the QR (isolated so a failure never blocks the print).
  const qrCanvas = await renderQrCanvas(CONTACT_URL, px(200));

  // ── Header ──────────────────────────────────────────────────────────────────
  c.lines(business?.name || 'Receipt', 40, 700, 'left');
  c.space(px(8));

  const addr = business?.businessInfo.address;
  const cityState = [addr?.city, addr?.state].filter(Boolean).join(', ');
  const addressLines = [
    addr?.street1,
    cityState,
    addr?.postalCode,
    business?.businessInfo.phone ? fPhone(business.businessInfo.phone) : '',
  ]
    .map((l) => (l || '').trim())
    .filter(Boolean);

  const date = toDate(checkout.details?.date);
  const metaLines = [
    date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }),
  ];
  if (checkout.employee?.name) metaLines.push(checkout.employee.name);

  c.cols(addressLines, metaLines, 26, 400);

  const orderDetails = mergeSaleOrderDetails(mergeSaleOrderDetails(getSaleOrderDetails(checkout), opts.orderDetails), checkout.orderDetails);
  const channelBand = getSaleReceiptChannelBand(
    checkout.salesChannel,
    orderDetails?.orderNumber
  );
  const fulfillmentLabel = orderDetails?.customerType
    ? { 'dine-in': 'IN STORE', 'to-go': 'TO GO' }[orderDetails.customerType]
    : undefined;
  const channelLabel = fulfillmentLabel ?? channelBand.channelLabel;
  c.rule();
  if (channelBand.orderLabel) {
    c.row(channelLabel, channelBand.orderLabel, 28, 600);
  } else {
    c.lines(channelLabel, 28, 600, 'center');
  }
  c.rule();

  if (orderDetails) {
    const clean = (value?: string | null) => {
      const trimmed = value?.trim();
      return trimmed && trimmed.toLowerCase() !== 'n/a' ? trimmed : '';
    };
    const customerName = clean(orderDetails.customerName);
    const tableNumber =
      orderDetails.customerType === 'dine-in' ? clean(orderDetails.tableNumber) : '';
    if (customerName) c.lines(`Customer: ${customerName}`, 26, 400, 'left');
    if (tableNumber) c.lines(`Table ${tableNumber}`, 26, 400, 'left');
    const notes = clean(orderDetails.orderNotes);
    if (notes) c.lines(`Notes: ${notes}`, 26, 400, 'left');
    if (customerName || tableNumber || notes) c.rule();
  }

  const receiptNo =
    checkout.shortIdentifier || orderDetails?.orderNumber || checkout.id?.slice(0, 8) || '';
  c.lines(`Receipt: ${receiptNo}`, 26, 400, 'left');
  c.rule();

  // Use the saved sale outcome: a failed attempt followed by a successful
  // retry must still produce a normal paid receipt.
  if (checkout.status === SourceStatusEnum.FAILED_FINAL) {
    c.lines('FAILED PAYMENT', 36, 700, 'center');
    c.rule();
  }

  // ── Items (name + price only — no identifiers) ───────────────────────────────
  checkout.items.forEach((item, idx) => {
    c.itemRow(item.name, fCurrency(item.price * item.quantity), 30, 600);

    if (item.quantity > 1) {
      c.lines(`${item.quantity} @ ${fCurrency(item.price)}`, 24, 400, 'left', px(20));
    }
    modifierNames(item).forEach((mod) => c.lines(mod, 24, 400, 'left', px(20)));

    const note = item.metadata?.specialInstructions;
    if (note) c.lines(`Note: ${note}`, 24, 400, 'left', px(20));

    if (idx < checkout.items.length - 1) c.space(px(12));
  });

  // ── Totals ────────────────────────────────────────────────────────────────
  const fin = checkout.financialInfo;
  const tip = (checkout as any).tippedAmount ?? fin.tip ?? 0;
  const clientPaysFees = checkout.details?.clientPaysFees;

  c.rule();
  c.row('Subtotal', fCurrency(fin.subTotal), 28, 400);
  const taxesAndFeesAmount = (fin.taxes ?? 0) + (clientPaysFees ? (fin.processingFees ?? 0) : 0);
  if (taxesAndFeesAmount > 0) c.row('Taxes & Fees', fCurrency(taxesAndFeesAmount), 28, 400);
  if (fin.discount > 0) c.row('Discount', `-${fCurrency(fin.discount)}`, 28, 400);
  if (fin.adjustment > 0) c.row('Adjustment', fCurrency(fin.adjustment), 28, 400);
  if (tip > 0) c.row('Tip', fCurrency(tip), 28, 400);

  c.rule();
  c.row('Total', fCurrency(fin.total + tip), 42, 700);

  // Captured payments only — refunded parents stay (the sale was still paid this
  // way), refund child rows print in the refund section below instead.
  const transactions = fin.payment?.transactions ?? [];
  selectCapturedPayments(transactions).forEach((tx) => {
    c.row(paymentLineLabel(tx), fCurrency(tx.amount), 28, 400);
    const refunded = tx.refundedAmount ?? 0;
    if (refunded > 0) c.lines(`Refunded -${fCurrency(refunded)}`, 24, 400, 'left', px(20));
  });

  // ── Refunds (settled only — pending/failed refunds never print) ──────────────
  const refundSummary = summarizeRefunds({
    transactions,
    saleStatus: checkout.status ?? undefined,
    grandTotal: fin.total + tip,
  });
  if (refundSummary.bannerLabel) {
    c.rule();
    c.lines(refundSummary.bannerLabel, 28, 700, 'center');
    c.space(px(6));
    // Legacy refunds have no child rows — the banner + totals still print.
    selectSettledRefunds(transactions).forEach((refund) => {
      const source = findRefundParent(refund, transactions) ?? refund;
      c.itemRow(`Refund ${paymentLineLabel(source)}`, `-${fCurrency(refund.amount)}`, 26, 400);
      if (refund.date) {
        const when = toDate(refund.date).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
        c.lines(when, 22, 400, 'left', px(20));
      }
    });
    c.space(px(6));
    c.row('Total Refunded', `-${fCurrency(refundSummary.refundedTotal)}`, 28, 600);
    c.row('Net Total', fCurrency(refundSummary.netTotal), 32, 700);
  }

  if (!orderDetails && checkout.customerInfo?.name) {
    c.space(px(8));
    c.lines(`Customer: ${checkout.customerInfo.name}`, 24, 400, 'left');
  }

  // ── Footer: custom policy → QR → PointCraft branding ─────────────────────────
  const footerText = resolveReceiptFooterText(business);
  if (footerText) {
    c.space(px(20));
    c.lines(footerText, 24, 400, 'center');
  }

  c.space(px(22));
  c.lines('Questions? Scan to contact us', 24, 400, 'center');
  c.space(px(12));
  c.image(qrCanvas, px(200));
  c.space(px(20));
  c.lines('Powered by PointCraft Inc', 23, 600, 'center');
  c.lines('Smart POS & Payments & Elevate Ai', 23, 400, 'center');
  c.lines('Email us: support@point-craft.com', 23, 400, 'center');
  c.lines('point-craft.com/support', 23, 400, 'center');

  return c.finish();
}
